import { useCallback, useEffect, useRef } from "react";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { useUploadOperationsDocuments } from "@features/vessel-call/vessel-call-status/Operations/hooks/useUploadOperationsDocuments";
import type { DisputeDocument } from "@services/disputes/types";
import { useTranslation } from "react-i18next";

const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

const toDocuments = (entries: UploadEntry[]): DisputeDocument[] =>
  entries.flatMap((entry) => {
    if (
      entry instanceof File ||
      typeof entry.url !== "string" ||
      !entry.url.trim() ||
      typeof entry.name !== "string" ||
      !entry.name.trim()
    ) {
      return [];
    }

    return [{ originalFileName: entry.name.trim(), url: entry.url.trim() }];
  });

type UseOperationsDocumentUploadOptions = {
  fieldName: string;
  certificateType: string;
  maxFiles: number;
  onDocumentsChange: (documents: DisputeDocument[]) => void;
};

export const useOperationsDocumentUpload = ({
  fieldName,
  certificateType,
  maxFiles,
  onDocumentsChange,
}: UseOperationsDocumentUploadOptions) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const uploadSessionRef = useRef(0);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const removeFileAtIndex = useUploadStore((state) => state.removeFileAtIndex);
  const files = useUploadStore((state) => state.filesByKey[fieldName] ?? EMPTY_UPLOAD_ENTRIES);
  const isUploading = useUploadStore((state) => (state.uploadsInFlightByKey[fieldName] ?? 0) > 0);
  const { mutate: uploadDocuments } = useUploadOperationsDocuments();

  const syncDocumentsFromStore = useCallback(() => {
    onDocumentsChange(toDocuments(useUploadStore.getState().getFilesForKey(fieldName)));
  }, [fieldName, onDocumentsChange]);

  const resetDocuments = useCallback(() => {
    uploadSessionRef.current += 1;
    clearFilesForKey(fieldName);
    onDocumentsChange([]);
  }, [clearFilesForKey, fieldName, onDocumentsChange]);

  useEffect(() => {
    resetDocuments();
  }, [resetDocuments]);

  const setFiles = useCallback(
    (nextFiles: UploadEntry[]) => {
      if (!nextFiles.length) {
        resetDocuments();
        return;
      }

      setFilesForKey(fieldName, nextFiles, { maxFiles });
      onDocumentsChange(toDocuments(nextFiles));
    },
    [fieldName, maxFiles, onDocumentsChange, resetDocuments, setFilesForKey],
  );

  const validateFiles = useCallback(
    (incomingFiles: File[]) => {
      const existingCount = useUploadStore.getState().getFilesForKey(fieldName).length;
      return existingCount + incomingFiles.length > maxFiles
        ? t("form-validation.max-files", { max: maxFiles })
        : null;
    },
    [fieldName, maxFiles, t],
  );

  const validateDrop = useCallback(
    (selectedCount: number) =>
      files.length + selectedCount > maxFiles
        ? t("form-validation.max-files", { max: maxFiles })
        : null,
    [files.length, maxFiles, t],
  );

  const handleUpload = useCallback(
    (_fieldName: string, uploadedFiles: File[]) => {
      const incomingFiles = Array.isArray(uploadedFiles)
        ? uploadedFiles.filter((entry): entry is File => entry instanceof File)
        : [];
      const currentEntries = useUploadStore.getState().getFilesForKey(fieldName);
      onDocumentsChange(toDocuments(currentEntries));
      if (!incomingFiles.length) return;

      const firstFileIndex = currentEntries.findIndex((entry) => entry === incomingFiles[0]);
      const startIndex =
        firstFileIndex >= 0
          ? firstFileIndex
          : Math.max(currentEntries.length - incomingFiles.length, 0);
      const uploadSession = uploadSessionRef.current;

      uploadDocuments(
        { files: incomingFiles, certificateType, storeKey: fieldName },
        {
          onSuccess: (documentUrls) => {
            if (uploadSession !== uploadSessionRef.current) return;

            const latestEntries = useUploadStore.getState().getFilesForKey(fieldName);
            const nextEntries = [...latestEntries];
            documentUrls.forEach((url, index) => {
              if (!url) return;
              const targetIndex = startIndex + index;
              const currentEntry = nextEntries[targetIndex];
              const uploadedFile = incomingFiles[index];
              nextEntries[targetIndex] = {
                name: currentEntry?.name ?? uploadedFile?.name ?? `document-${targetIndex + 1}`,
                url,
                type: currentEntry instanceof File ? currentEntry.type : currentEntry?.type,
                certificateType,
                idx: targetIndex,
              };
            });
            setFilesForKey(fieldName, nextEntries, { maxFiles });
            onDocumentsChange(toDocuments(nextEntries));
          },
          onError: () => {
            if (uploadSession !== uploadSessionRef.current) return;

            notify({
              title: t("vessel-management.notifications.attachments-upload-error"),
              description: t(
                "vessel-management.notifications.attachments-upload-error-description",
              ),
              type: "error",
            });
            Array.from({ length: incomingFiles.length }, (_value, index) => startIndex + index)
              .reverse()
              .forEach((index) => removeFileAtIndex(fieldName, index));
            syncDocumentsFromStore();
          },
        },
      );
    },
    [
      certificateType,
      fieldName,
      maxFiles,
      notify,
      onDocumentsChange,
      removeFileAtIndex,
      setFilesForKey,
      syncDocumentsFromStore,
      t,
      uploadDocuments,
    ],
  );

  return {
    files,
    isUploading,
    setFiles,
    handleUpload,
    validateFiles,
    validateDrop,
    syncDocumentsFromStore,
    resetDocuments,
  };
};
